import {
  ComponentRenderMap,
  SDUIUPLTProps,
} from "@faire/retailer-visitor-shared/components/SDUI/types";
import { Viewport } from "@faire/web--source/ui/hooks/viewport-utils/viewport-utils";
import { IComponent } from "@faire/web-api--source/indigofair/data/IComponent";
import { IComponentLoggingData } from "@faire/web-api--source/indigofair/data/IComponentLoggingData";
import { IGetSDUIFilteredComponentsResponse } from "@faire/web-api--source/indigofair/data/IGetSDUIFilteredComponentsResponse";
import { IGetSDUIFiltersResponse } from "@faire/web-api--source/indigofair/data/IGetSDUIFiltersResponse";
import { ISection } from "@faire/web-api--source/indigofair/data/ISection";
import { ISectionLoggingData } from "@faire/web-api--source/indigofair/data/ISectionLoggingData";
import { IViewLoggingData } from "@faire/web-api--source/indigofair/data/IViewLoggingData";
import React from "react";

export interface SDUIComponentRenderBehaviour {
  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/loading
  imagesLoadingStrategy?: HTMLImageElement["loading"];
  /* Fetch Priority is not yet supported in all browsers, and React 19 and
    Styled Components 6 to use it.
    https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/fetchPriority
    While this property is not being used, it is being kept around as a comment to show
    how other rendering hints can be provided to the SDUIComponentRenderer
  */
  // imagesfetchPriority: HTMLImageElement["fetchPriority"];
  deferInitializationUntilView?: boolean;
  /**
   * If true, the product grid will wait for the scroll to top event before
   * rendering the first batch of tiles.
   */
  gridOptimizations?: boolean;
}
export interface SDUIComponentInstanceRendererProps {
  component: IComponent;
  loggingData: ComponentLoggingEventParams;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  onClickClientDismissHandler?: () => void;
}

export interface SDUISectionInstanceRendererProps {
  section: ISection;
  componentRenderers: ComponentRenderMap;
  sectionLoggingData: SectionLoggingEventParams;
  loadTimingRef?: React.RefCallback<HTMLElement>;
  // performance related props. Existence to be re-evaluated after SD UI supports lazy loading
  shouldRenderBelowTheFold?: boolean;
  UPLTprops?: SDUIUPLTProps;
  //rsc page related props
  pageParams?: PageParams;
  onClickClientDismissHandler?: () => void;
  // Prop to override the data fetching for Filtered Components in the Filterable Paginated Section
  filteredComponentsPromise?: Promise<IGetSDUIFilteredComponentsResponse>;
  // Prop to override the data fetching for Filters in the Filterable Paginated Section
  filtersPromise?: Promise<IGetSDUIFiltersResponse>;
  // Prop to indicate if the section is a Filterable Paginated Section
  isFilterablePaginatedSection?: boolean;
  // Pass viewport as a prop so the layout renderer to be rendered on the client or server
  viewport?: Viewport;
}

export interface PageParams {
  filterKeys: string[];
  pageNumber: number;
  uniquePageKey?: string;
  shouldIgnoreImagesForUPLT?: boolean;
}

export type SectionLoggingEventParams = IViewLoggingData & ISectionLoggingData;
export namespace SectionLoggingEventParams {
  export const build = (
    partial?: Partial<SectionLoggingEventParams>
  ): SectionLoggingEventParams => {
    return {
      ...IViewLoggingData.build(partial),
      ...ISectionLoggingData.build(partial),
    };
  };
}

export type ComponentLoggingEventParams = SectionLoggingEventParams &
  IComponentLoggingData & {
    component_id: string | undefined;
  };
export namespace ComponentLoggingEventParams {
  export const build = (
    partial?: Partial<ComponentLoggingEventParams>
  ): ComponentLoggingEventParams => {
    return {
      ...SectionLoggingEventParams.build(partial),
      ...IComponentLoggingData.build(partial),
      component_id: partial?.component_id,
    };
  };
}
export type ActionLoggingEventParams = ComponentLoggingEventParams & {
  action_content: string | undefined;
  action_positions: string | undefined;
  action_instance_identifier: string | undefined;
};
export namespace ActionLoggingEventParams {
  export const build = (
    partial?: Partial<ActionLoggingEventParams>
  ): ActionLoggingEventParams => {
    return {
      ...ComponentLoggingEventParams.build(partial),
      action_content: partial?.action_content,
      action_positions: partial?.action_positions,
      action_instance_identifier: partial?.action_instance_identifier,
    };
  };
}

export type LoggingEventParams =
  | SectionLoggingEventParams
  | ComponentLoggingEventParams
  | ActionLoggingEventParams;
